import React, { useState, useEffect } from 'react';
import { FaUsers, FaUserGraduate, FaChalkboardTeacher, FaEye, FaDownload, FaFileExcel } from 'react-icons/fa';
import { databaseService, OrganizationData, Teacher, Student, Course } from '../../services/databaseService';

interface OrganizationDataViewerProps {
  organization: OrganizationData;
}

export const OrganizationDataViewer: React.FC<OrganizationDataViewerProps> = ({ organization }) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'teachers' | 'students' | 'courses'>('overview');

  useEffect(() => {
    loadOrganizationData();
  }, [organization.tenantKey]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      console.log(`🔄 Cargando datos para organización: ${organization.displayName} (${organization.tenantKey})`);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      // Cargar datos usando los endpoints específicos de la organización
      const [usersResponse, studentsResponse, coursesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/organizations/${organization.tenantKey}/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/api/organizations/${organization.tenantKey}/students`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/api/organizations/${organization.tenantKey}/courses`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      // Procesar respuestas
      const usersData = usersResponse.ok ? await usersResponse.json() : { users: [] };
      const studentsData = studentsResponse.ok ? await studentsResponse.json() : { students: [] };
      const coursesData = coursesResponse.ok ? await coursesResponse.json() : { courses: [] };

      // Mapear usuarios a formato Teacher
      const mappedTeachers: Teacher[] = (usersData.users || []).map((user: any) => ({
        _id: user._id,
        username: user.username || user.email,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role,
        organizationId: organization._id,
        courses: user.allowedCourses || [],
        phone: user.phone || '',
        isActive: user.isActive !== undefined ? user.isActive : true,
        mustChangePassword: user.mustChangePassword !== undefined ? user.mustChangePassword : false,
        password: '***',
        lastLogin: user.lastLogin || '',
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString()
      }));

      // Mapear estudiantes a formato Student
      const mappedStudents: Student[] = (studentsData.students || []).map((student: any) => ({
        _id: student._id,
        id: student.id || student._id,
        name: student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
        firstName: student.firstName || '',
        lastName: student.lastName || '',
        course: student.course || '',
        email: student.email || '',
        gender: student.gender || '',
        organizationId: organization._id,
        skillLevels: student.skillLevels || [],
        createdAt: student.createdAt || new Date().toISOString(),
        updatedAt: student.updatedAt || new Date().toISOString()
      }));

      // Mapear cursos a formato Course
      const mappedCourses: Course[] = (coursesData.courses || []).map((course: any) => ({
        _id: course._id,
        name: course.name || course.courseName || '',
        level: course.level || '',
        teacherIds: course.teacherIds || [],
        studentCount: course.studentCount || 0,
        organizationId: organization._id,
        createdAt: course.createdAt || new Date().toISOString(),
        updatedAt: course.updatedAt || new Date().toISOString()
      }));

      setTeachers(mappedTeachers);
      setStudents(mappedStudents);
      setCourses(mappedCourses);

      console.log(`✅ Datos cargados: ${mappedTeachers.length} usuarios, ${mappedStudents.length} estudiantes, ${mappedCourses.length} cursos`);

    } catch (error) {
      console.error('❌ Error loading organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: FaEye },
    { id: 'teachers', label: 'Profesores', icon: FaChalkboardTeacher },
    { id: 'students', label: 'Estudiantes', icon: FaUserGraduate },
    { id: 'courses', label: 'Cursos', icon: FaUsers }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Datos de {organization.displayName}
            </h3>
            <p className="text-sm text-gray-600">
              Visualiza y gestiona los datos de la organización
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              organization.status === 'active' ? 'bg-green-100 text-green-800' :
              organization.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {organization.status === 'active' ? 'Activa' : 
               organization.status === 'trial' ? 'Prueba' : 'Suspendida'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="bg-blue-500 p-3 rounded-full mr-4">
                          <FaChalkboardTeacher className="text-white w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-blue-600">{teachers.length}</div>
                          <div className="text-sm text-blue-600">Profesores</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="bg-green-500 p-3 rounded-full mr-4">
                          <FaUserGraduate className="text-white w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{students.length}</div>
                          <div className="text-sm text-green-600">Estudiantes</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="bg-purple-500 p-3 rounded-full mr-4">
                          <FaUsers className="text-white w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-purple-600">{courses.length}</div>
                          <div className="text-sm text-purple-600">Cursos</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Organization Info */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Información de la Organización</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Clave del Tenant</div>
                        <div className="font-medium">{organization.tenantKey}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Dominios</div>
                        <div className="font-medium">{organization.domains.join(', ')}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Creada</div>
                        <div className="font-medium">{new Date(organization.createdAt).toLocaleDateString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Última actualización</div>
                        <div className="font-medium">{new Date(organization.updatedAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>

                  {/* Branding Preview */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Configuración de Marca</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-gray-600 mb-2">Color Primario</div>
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-8 h-8 rounded border border-gray-300"
                            style={{ 
                              backgroundColor: organization.branding?.colors?.primary || 
                                             (organization.branding as any)?.primaryColor || 
                                             '#1e40af' 
                            }}
                          ></div>
                          <span className="font-mono text-sm">
                            {organization.branding?.colors?.primary || 
                             (organization.branding as any)?.primaryColor || 
                             '#1e40af'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 mb-2">Color Secundario</div>
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-8 h-8 rounded border border-gray-300"
                            style={{ 
                              backgroundColor: organization.branding?.colors?.secondary || 
                                             (organization.branding as any)?.secondaryColor || 
                                             '#3b82f6' 
                            }}
                          ></div>
                          <span className="font-mono text-sm">
                            {organization.branding?.colors?.secondary || 
                             (organization.branding as any)?.secondaryColor || 
                             '#3b82f6'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 mb-2">Color de Acento</div>
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-8 h-8 rounded border border-gray-300"
                            style={{ 
                              backgroundColor: organization.branding?.colors?.accent || 
                                             (organization.branding as any)?.accentColor || 
                                             '#f59e0b' 
                            }}
                          ></div>
                          <span className="font-mono text-sm">
                            {organization.branding?.colors?.accent || 
                             (organization.branding as any)?.accentColor || 
                             '#f59e0b'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'teachers' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Profesores ({teachers.length})</h4>
                    <button
                      onClick={() => exportToCSV(teachers, `profesores_${organization.tenantKey}`)}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <FaFileExcel className="w-4 h-4" />
                      <span>Exportar CSV</span>
                    </button>
                  </div>

                  {teachers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cursos</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Último Acceso</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {teachers.map((teacher) => (
                            <tr key={teacher._id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900">
                                  {teacher.firstName} {teacher.lastName}
                                </div>
                                <div className="text-sm text-gray-500">@{teacher.username}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {teacher.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  teacher.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                  teacher.role === 'teacher' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {teacher.role === 'admin' ? 'Administrador' :
                                   teacher.role === 'teacher' ? 'Profesor' : 'Visualizador'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {teacher.courses?.join(', ') || 'Sin asignar'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  teacher.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {teacher.isActive ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {teacher.lastLogin ? 
                                  new Date(teacher.lastLogin).toLocaleDateString() : 
                                  'Nunca'
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaChalkboardTeacher className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600">No hay profesores registrados</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'students' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Estudiantes ({students.length})</h4>
                    <button
                      onClick={() => exportToCSV(students, `estudiantes_${organization.tenantKey}`)}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <FaFileExcel className="w-4 h-4" />
                      <span>Exportar CSV</span>
                    </button>
                  </div>

                  {students.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID/RUT</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Curso</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Género</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creado</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {students.map((student) => (
                            <tr key={student._id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {student.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900">{student.name}</div>
                                {student.firstName && student.lastName && (
                                  <div className="text-sm text-gray-500">
                                    {student.firstName} {student.lastName}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {student.course}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {student.email || 'Sin email'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {student.gender || 'No especificado'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(student.createdAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaUserGraduate className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600">No hay estudiantes registrados</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'courses' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Cursos ({courses.length})</h4>
                    <button
                      onClick={() => exportToCSV(courses, `cursos_${organization.tenantKey}`)}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <FaFileExcel className="w-4 h-4" />
                      <span>Exportar CSV</span>
                    </button>
                  </div>

                  {courses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {courses.map((course) => (
                        <div key={course._id} className="bg-gray-50 rounded-lg p-4">
                          <h5 className="font-medium text-gray-900 mb-2">{course.name}</h5>
                          <div className="space-y-1 text-sm text-gray-600">
                            <div>Nivel: {course.level}</div>
                            <div>Estudiantes: {course.studentCount}</div>
                            <div>Profesores: {course.teacherIds.length}</div>
                            <div>Creado: {new Date(course.createdAt).toLocaleDateString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600">No hay cursos registrados</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationDataViewer;